/**
 * Orders Redux Slice
 * Manages order state (orders list, order status)
 * 
 * ⚠️ IMPORTANT: Per ADR-FE-001, this slice only stores order data from backend.
 * All order operations are handled by backend Order Service.
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { Order, CreateOrderParams, mapBackendOrderToOrder } from '../../types/order';

export interface OrdersState {
  orders: Order[];
  byId: Record<string, Order>; // Normalized by platform_order_id
  loading: boolean;
  error: string | null;
  placingOrder: boolean; // Track order placement in progress
  placingOrderProgress: Record<string, 'pending' | 'success' | 'error'>; // Track per-broker progress
}

const initialState: OrdersState = {
  orders: [],
  byId: {},
  loading: false,
  error: null,
  placingOrder: false,
  placingOrderProgress: {},
};

/**
 * Fetch orders list from backend
 */
export const fetchOrders = createAsyncThunk(
  'orders/fetch',
  async (params: { status?: string; instrument_id?: string; limit?: number; offset?: number } = {}, { rejectWithValue }) => {
    try {
      const { api } = await import('../../api/client');
      const response = await api.ordersList(
        params.status,
        params.instrument_id,
        params.limit,
        params.offset
      );

      // Map backend responses to frontend types
      const orders: Order[] = (response.data.orders || []).map((order: any) => {
        const mapped = mapBackendOrderToOrder(order);
        return mapped || ({} as Order);
      }).filter((order: Order) => order.platform_order_id);

      return orders;
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch orders';
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Fetch single order by platform_order_id
 */
export const fetchOrder = createAsyncThunk(
  'orders/fetchOne',
  async (platformOrderId: string, { rejectWithValue }) => {
    try {
      const { api } = await import('../../api/client');
      const response = await api.ordersGet(platformOrderId);

      const mapped = mapBackendOrderToOrder(response.data);
      if (!mapped) {
        throw new Error('Invalid order response');
      }

      return mapped;
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch order';
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Place a single order
 */
export const placeOrder = createAsyncThunk(
  'orders/place',
  async (params: CreateOrderParams, { rejectWithValue }) => {
    try {
      const { api } = await import('../../api/client');
      const requestBody: any = {
        instrument_id: params.instrument_id,
        side: params.side,
        quantity: params.quantity,
        order_type: params.order_type,
        price: params.price,
        stop_price: params.stop_price,
        time_in_force: params.time_in_force,
        client_order_id: params.client_order_id,
        strategy_id: params.strategy_id,
        notes: params.notes,
        order_tags: params.order_tags,
        routing_preferences: params.routing_preferences,
      };
      
      // Prefer registration_id (system UUID) over broker_account_id + broker_type
      if (params.registration_id) {
        requestBody.registration_id = params.registration_id;
      } else if (params.broker_account_id && params.broker_type) {
        // Fallback for backward compatibility
        requestBody.broker_account_id = params.broker_account_id;
        requestBody.broker_type = params.broker_type;
      } else {
        throw new Error('Either registration_id or both broker_account_id and broker_type must be provided');
      }
      
      const response = await api.ordersCreate(requestBody);

      const mapped = mapBackendOrderToOrder(response.data);
      if (!mapped) {
        throw new Error('Invalid order response');
      }

      return mapped;
    } catch (error: unknown) {
      // Extract detailed error message from backend with user-friendly fields
      let errorMessage = 'Failed to place order';
      let errorDetails: any = null;
      
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as any;
        const responseData = axiosError.response?.data;
        
        // Check for validation errors from backend
        if (responseData?.error?.details?.validation_errors) {
          const validationErrors = responseData.error.details.validation_errors;
          const errorMessages = validationErrors.map((err: any) => {
            const field = err.loc?.join('.') || 'field';
            const msg = err.msg || 'Invalid value';
            return `${field}: ${msg}`;
          });
          errorMessage = `Validation failed: ${errorMessages.join('; ')}`;
        } else if (responseData?.error) {
          // Use user-friendly message if available, fallback to regular message
          errorMessage = responseData.error.user_message || responseData.error.message || 'Failed to place order';
          
          // Extract structured error details for UI display
          errorDetails = {
            userMessage: responseData.error.user_message || responseData.error.message,
            canRetry: responseData.error.can_retry ?? false,
            nextSteps: responseData.error.next_steps,
            errorCategory: responseData.error.error_category,
            severity: responseData.error.severity,
            errorCode: responseData.error.code,
            originalMessage: responseData.error.details?.broker_message || responseData.error.message
          };
        } else if (axiosError.response?.status === 400) {
          errorMessage = responseData?.error?.message || 'Invalid request. Please check your order details.';
        }
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      
      console.error('Order placement error:', error, errorDetails);
      
      // Return error message with details for UI to use
      return rejectWithValue({
        message: errorMessage,
        details: errorDetails
      });
    }
  }
);

/**
 * Cancel an order
 */
export const cancelOrder = createAsyncThunk(
  'orders/cancel',
  async (platformOrderId: string, { rejectWithValue }) => {
    try {
      const { api } = await import('../../api/client');
      const response = await api.ordersCancel(platformOrderId);

      const mapped = mapBackendOrderToOrder(response.data);
      if (!mapped) {
        throw new Error('Invalid order response');
      }

      return mapped;
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to cancel order';
      return rejectWithValue(errorMessage);
    }
  }
);

export const ordersSlice = createSlice({
  name: 'orders',
  initialState,
  reducers: {
    setOrders: (state, action: { payload: Order[] }) => {
      state.orders = action.payload;
      // Update normalized byId
      state.byId = {};
      action.payload.forEach((order) => {
        state.byId[order.platform_order_id] = order;
      });
    },
    addOrder: (state, action: { payload: Order }) => {
      // Add to beginning of array
      state.orders.unshift(action.payload);
      // Update normalized byId
      state.byId[action.payload.platform_order_id] = action.payload;
    },
    updateOrder: (state, action: { payload: Order }) => {
      const index = state.orders.findIndex((o) => o.platform_order_id === action.payload.platform_order_id);
      if (index !== -1) {
        state.orders[index] = action.payload;
      } else {
        // Add if not found
        state.orders.unshift(action.payload);
      }
      // Update normalized byId
      state.byId[action.payload.platform_order_id] = action.payload;
    },
    setLoading: (state, action: { payload: boolean }) => {
      state.loading = action.payload;
    },
    setError: (state, action: { payload: string | null }) => {
      state.error = action.payload;
    },
    clearOrders: (state) => {
      state.orders = [];
      state.byId = {};
      state.error = null;
    },
    setPlacingOrder: (state, action: { payload: boolean }) => {
      state.placingOrder = action.payload;
    },
    setPlacingOrderProgress: (state, action: { payload: { brokerAccountId: string; status: 'pending' | 'success' | 'error' } }) => {
      state.placingOrderProgress[action.payload.brokerAccountId] = action.payload.status;
    },
    clearPlacingOrderProgress: (state) => {
      state.placingOrderProgress = {};
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch orders
      .addCase(fetchOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrders.fulfilled, (state, action) => {
        state.orders = action.payload;
        // Update normalized byId
        state.byId = {};
        action.payload.forEach((order) => {
          state.byId[order.platform_order_id] = order;
        });
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch single order
      .addCase(fetchOrder.fulfilled, (state, action) => {
        const index = state.orders.findIndex((o) => o.platform_order_id === action.payload.platform_order_id);
        if (index !== -1) {
          state.orders[index] = action.payload;
        } else {
          state.orders.unshift(action.payload);
        }
        state.byId[action.payload.platform_order_id] = action.payload;
      })
      // Place order
      .addCase(placeOrder.pending, (state) => {
        state.placingOrder = true;
        state.error = null;
      })
      .addCase(placeOrder.fulfilled, (state, action) => {
        state.orders.unshift(action.payload);
        state.byId[action.payload.platform_order_id] = action.payload;
        state.placingOrder = false;
        state.error = null;
        // Success message is available in action.payload.message if needed
      })
      .addCase(placeOrder.rejected, (state, action) => {
        state.placingOrder = false;
        // Handle both string errors (backward compatibility) and structured errors
        if (typeof action.payload === 'object' && action.payload !== null && 'message' in action.payload) {
          state.error = (action.payload as any).message;
        } else {
          state.error = action.payload as string;
        }
      })
      // Cancel order
      .addCase(cancelOrder.fulfilled, (state, action) => {
        const index = state.orders.findIndex((o) => o.platform_order_id === action.payload.platform_order_id);
        if (index !== -1) {
          state.orders[index] = action.payload;
        }
        state.byId[action.payload.platform_order_id] = action.payload;
      });
  },
});

export const {
  setOrders,
  addOrder,
  updateOrder,
  setLoading,
  setError,
  clearOrders,
  setPlacingOrder,
  setPlacingOrderProgress,
  clearPlacingOrderProgress,
} = ordersSlice.actions;
export default ordersSlice.reducer;

